import {
	CORRECTION_STATUSES,
	FEEDBACK_KIND,
	FEEDBACK_STATUSES,
	type FeedbackItem,
	type FeedbackKind,
	type FeedbackNote,
} from '@thom/folio-domain/core/domain/feedback'
import { issueId } from '@thom/folio-domain/core/domain/issue'
import { userId } from '@thom/folio-domain/core/domain/project'
import type { FeedbackPort } from '@thom/folio-domain/core/ports/feedback'
import { err, ok, type Result } from '@thom/folio-domain/lib/result'
import { type ErrorResponse, tryCatch } from '@thom/folio-domain/lib/try-catch'
import { z } from '@thom/folio-domain/lib/zod'
import { getPocketBaseClient } from './client'

const THREADS = 'journ_feedback_threads'
const DOCS = 'journ_feedback_docs'
const BASE_PATH = '/api/folio'

const noteSchema = z.object({
	id: z.string(),
	author: z.string().default(''),
	content: z.string(),
	timestamp: z.number(),
})

const anchorSchema = z.object({ page: z.string() })

const threadSchema = z.object({
	id: z.string(),
	host: z.string(),
	anchor: anchorSchema,
	messages: z.array(noteSchema).default([]),
	status: z.enum(FEEDBACK_STATUSES),
	issueId: z.string().optional(),
	author: z.string().default(''),
	createdAt: z.number(),
})

const docSchema = z.object({
	id: z.string(),
	host: z.string(),
	anchor: anchorSchema,
	content: z.string(),
	corrections: z.array(noteSchema.extend({ status: z.enum(CORRECTION_STATUSES) })).default([]),
	status: z.enum(FEEDBACK_STATUSES),
	issueId: z.string().optional(),
	author: z.string().default(''),
	createdAt: z.number(),
})

const listSchema = z.object({ threads: z.array(threadSchema).default([]), docs: z.array(docSchema).default([]) })

const ticketSchema = z.object({ id: z.string(), slug: z.string() })

const toNote = (wire: z.infer<typeof noteSchema>): FeedbackNote => ({
	id: wire.id,
	author: wire.author ? userId(wire.author) : undefined,
	content: wire.content,
	at: new Date(wire.timestamp),
})

const toThread = (wire: z.infer<typeof threadSchema>): FeedbackItem => ({
	kind: FEEDBACK_KIND.THREAD,
	id: wire.id,
	host: wire.host,
	page: wire.anchor.page,
	status: wire.status,
	issueId: wire.issueId ? issueId(wire.issueId) : undefined,
	author: wire.author ? userId(wire.author) : undefined,
	createdAt: new Date(wire.createdAt),
	summary: wire.messages[0]?.content ?? '',
	messages: wire.messages.map(toNote),
	corrections: [],
})

const toDoc = (wire: z.infer<typeof docSchema>): FeedbackItem => ({
	kind: FEEDBACK_KIND.DOC,
	id: wire.id,
	host: wire.host,
	page: wire.anchor.page,
	status: wire.status,
	issueId: wire.issueId ? issueId(wire.issueId) : undefined,
	author: wire.author ? userId(wire.author) : undefined,
	createdAt: new Date(wire.createdAt),
	summary: wire.content,
	messages: [],
	corrections: wire.corrections.map(correction => ({ ...toNote(correction), status: correction.status })),
})

const message = (error: ErrorResponse): string => {
	const served = (error.originalError as { response?: { message?: unknown } } | undefined)?.response?.message
	return typeof served === 'string' && served ? served : error.message
}

const itemPath = (kind: FeedbackKind, id: string) =>
	`${BASE_PATH}/feedback/${kind === FEEDBACK_KIND.DOC ? 'docs' : 'threads'}/${encodeURIComponent(id)}`

const createFeedbackAdapter = (): FeedbackPort => {
	const client = getPocketBaseClient()

	return {
		list: async (project, options): Promise<Result<readonly FeedbackItem[]>> => {
			const query = options?.dismissed ? '?dismissed=1' : ''
			const { data, error } = await tryCatch(
				client.send(`${BASE_PATH}/projects/${encodeURIComponent(project)}/feedback${query}`, { method: 'GET' }),
			)
			if (error) return err(new Error(`Failed to load the feedback: ${message(error)}`, { cause: error }))
			const parsed = listSchema.safeParse(data)
			if (!parsed.success) return err(new Error('The server sent an unexpected answer.'))
			return ok([...parsed.data.threads.map(toThread), ...parsed.data.docs.map(toDoc)])
		},
		dismiss: async (kind, id): Promise<Result<void>> => {
			const { error } = await tryCatch(client.send(`${itemPath(kind, id)}/dismiss`, { method: 'POST' }))
			return error ? err(new Error(`Could not dismiss: ${message(error)}`, { cause: error })) : ok(undefined)
		},
		createTicket: async (kind, id) => {
			const { data, error } = await tryCatch(client.send(`${itemPath(kind, id)}/ticket`, { method: 'POST' }))
			if (error) return err(new Error(`Could not create the ticket: ${message(error)}`, { cause: error }))
			const parsed = ticketSchema.safeParse(data)
			return parsed.success ? ok(parsed.data) : err(new Error('The server sent an unexpected answer.'))
		},
	}
}

export { createFeedbackAdapter, DOCS, THREADS }
